import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, HStack, Icon, Link, ListRow, Sheet, Text, VStack } from '@betterment/samba-react';
import { CardLoader } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { GainsAndLossesDocument } from './graphql/query.gainsandlosses.generated';

import urlBuilder from '../../../Shared/url-builder';

export const GainsAndLossesCard: React.FC = () => {
  const householdId = useRequiredParam('householdId');
  const { data } = useGraphQLQuery(GainsAndLossesDocument, { variables: { id: householdId } });

  if (!data) return <CardLoader />;
  return (
    <Card>
      <Card.Header css={{ padding: 'space.300' }}>
        <H2 variant="title200">YTD net realized gains</H2>
      </Card.Header>
      <Card.Content css={{ paddingX: 'space.300', paddingBottom: 'space.150' }}>
        {data.household.annualGainsSummaries.map(summary => (
          <Sheet overlay="weak" key={summary.identifyingTaxpayerId}>
            <Sheet.Trigger asChild={true}>
              <div style={{ cursor: 'pointer' }}>
                <ListRow
                  css={{
                    borderTop: '1px solid',
                    borderColor: 'border.weak',
                    paddingX: '0',
                    _hover: { backgroundColor: 'background' },
                  }}
                >
                  <ListRow.LeadingContent>
                    <Text variant="text100">{summary.name}</Text>
                  </ListRow.LeadingContent>
                  <ListRow.TrailingContent>
                    <Text variant="text100">{formatMoney(summary.yearToDateNetRealizedGains)}</Text>
                    {summary.annualGainsAllowance ? (
                      <Text variant="text100" color="text.weak">
                        {formatMoney(
                          toMoney(summary.annualGainsAllowance.cents - summary.yearToDateNetRealizedGains.cents)
                        )}{' '}
                        remaining gains allowance
                      </Text>
                    ) : (
                      <Text variant="text100" color="text.weak">
                        No gains allowance.&nbsp;
                        <Link
                          href={urlBuilder.getGainsAllowanceEditPage.absolute({
                            taxpayerId: summary.identifyingTaxpayerId,
                            household_id: householdId,
                          })}
                          onClick={e => {
                            // Prevent the click from bubbling up and triggering the Sheet
                            e.stopPropagation();
                          }}
                        >
                          Add a gains allowance
                        </Link>
                      </Text>
                    )}
                  </ListRow.TrailingContent>
                  <ListRow.TrailingAsset>
                    <Icon name="CaretRight" size="small" />
                  </ListRow.TrailingAsset>
                </ListRow>
              </div>
            </Sheet.Trigger>
            <Sheet.Content
              css={{ overflowY: 'auto', padding: 'space.300', minWidth: '480px' }}
              aria-describedby={undefined}
            >
              <VStack css={{ paddingY: 'space.100' }}>
                <Sheet.Title>YTD realized gain/loss</Sheet.Title>
                <ListRow css={{ px: '0', py: '16px' }}>
                  <ListRow.LeadingContent>Client</ListRow.LeadingContent>
                  <ListRow.TrailingContent>{summary.name}</ListRow.TrailingContent>
                </ListRow>

                <ListRow
                  css={{
                    padding: 'space.0',
                    paddingX: 'space.0',
                    paddingY: 'space.200',
                    borderTopWidth: 'thin',
                    borderTopColor: 'border.weak',
                    borderTopStyle: 'solid',
                  }}
                >
                  <ListRow.LeadingContent>Net gains YTD</ListRow.LeadingContent>
                  <ListRow.TrailingContent>{formatMoney(summary.yearToDateNetRealizedGains)}</ListRow.TrailingContent>
                </ListRow>

                <ListRow
                  css={{
                    padding: 'space.0',
                    paddingX: 'space.0',
                    paddingY: 'space.200',
                    borderTopWidth: 'thin',
                    borderTopColor: 'border.weak',
                    borderTopStyle: 'solid',
                  }}
                >
                  <ListRow.LeadingContent>Short term net gain YTD</ListRow.LeadingContent>
                  <ListRow.TrailingContent>{formatMoney(summary.shortTerm.netAmount)}</ListRow.TrailingContent>
                  <ListRow.BottomContent css={{ mt: '0' }}>
                    <HStack css={{ justifyContent: 'space-between', py: 'space.50' }}>
                      <Text variant="text50" color="text.weak">
                        Short term realized gain
                      </Text>
                      <Text variant="text50" color="text.weak">
                        {formatMoney(summary.shortTerm.gainAmount)}
                      </Text>
                    </HStack>
                    <HStack css={{ justifyContent: 'space-between' }}>
                      <Text variant="text50" color="text.weak">
                        Short term realized loss
                      </Text>
                      <Text variant="text50" color="text.weak">
                        {formatMoney(summary.shortTerm.lossAmount)}
                      </Text>
                    </HStack>
                  </ListRow.BottomContent>
                </ListRow>

                <ListRow
                  css={{
                    padding: 'space.0',
                    paddingX: 'space.0',
                    paddingY: 'space.200',
                    borderTopWidth: 'thin',
                    borderTopColor: 'border.weak',
                    borderTopStyle: 'solid',
                  }}
                >
                  <ListRow.LeadingContent>Long term net gain YTD</ListRow.LeadingContent>
                  <ListRow.TrailingContent>{formatMoney(summary.longTerm.netAmount)}</ListRow.TrailingContent>
                  <ListRow.BottomContent css={{ mt: '0' }}>
                    <HStack css={{ justifyContent: 'space-between', py: 'space.50' }}>
                      <Text variant="text50" color="text.weak">
                        Long term realized gain
                      </Text>
                      <Text variant="text50" color="text.weak">
                        {formatMoney(summary.longTerm.gainAmount)}
                      </Text>
                    </HStack>
                    <HStack css={{ justifyContent: 'space-between' }}>
                      <Text variant="text50" color="text.weak">
                        {' '}
                        Long term realized loss
                      </Text>
                      <Text variant="text50" color="text.weak">
                        {formatMoney(summary.longTerm.lossAmount)}
                      </Text>
                    </HStack>
                  </ListRow.BottomContent>
                </ListRow>

                {summary.annualGainsAllowance ? (
                  <ListRow
                    css={{
                      padding: 'space.0',
                      paddingX: 'space.0',
                      paddingY: 'space.200',
                      borderTopWidth: 'thin',
                      borderTopColor: 'border.weak',
                      borderTopStyle: 'solid',
                    }}
                  >
                    <ListRow.LeadingContent>Remaining gains allowance</ListRow.LeadingContent>
                    <ListRow.TrailingContent>
                      {formatMoney(
                        toMoney(summary.annualGainsAllowance.cents - summary.yearToDateNetRealizedGains.cents)
                      )}
                      <Text variant="text50" color="text.weak">
                        of {formatMoney(summary.annualGainsAllowance)}
                      </Text>
                      <Link
                        href={urlBuilder.getGainsAllowanceEditPage.absolute({
                          taxpayerId: summary.identifyingTaxpayerId,
                          household_id: householdId,
                        })}
                        css={{ fontSize: 'fontSize.50' }}
                      >
                        Edit
                      </Link>
                    </ListRow.TrailingContent>
                  </ListRow>
                ) : (
                  <ListRow
                    css={{
                      padding: 'space.0',
                      paddingX: 'space.0',
                      paddingY: 'space.200',
                      borderTopWidth: 'thin',
                      borderTopColor: 'border.weak',
                      borderTopStyle: 'solid',
                    }}
                  >
                    <ListRow.LeadingContent>Remaining gains allowance</ListRow.LeadingContent>
                    <ListRow.TrailingContent>
                      <Text variant="text100">None</Text>
                      <Link
                        href={urlBuilder.getGainsAllowanceEditPage.absolute({
                          taxpayerId: summary.identifyingTaxpayerId,
                          household_id: householdId,
                        })}
                        css={{ fontSize: 'fontSize.50' }}
                      >
                        Add a gains allowance
                      </Link>
                    </ListRow.TrailingContent>
                  </ListRow>
                )}
              </VStack>
            </Sheet.Content>
          </Sheet>
        ))}
      </Card.Content>
    </Card>
  );
};
